import React from 'react';
import './ExamStatusCard.css';
import ExamBadge from './ExamBadge';
import { examCodeOf } from '../utils/examCatalog';

interface ExamStatusCardProps {
  examTypes: any[];
  currentSession: any;
  examHistory: any[];
  onStartExam: () => void;
  onContinueExam: (sessionId: string) => void;
}

const ExamStatusCard: React.FC<ExamStatusCardProps> = ({
  examTypes,
  currentSession,
  examHistory,
  onStartExam,
  onContinueExam
}) => {
  const formatTime = (minutes: number) => {
    const safeMinutes = Number.isFinite(minutes) ? minutes : 0;
    const hours = Math.floor(safeMinutes / 60);
    const mins = safeMinutes % 60;
    return hours > 0 ? `${hours}시간 ${mins}분` : `${mins}분`;
  };

  const getProgressPercentage = (answered: number, total: number) => {
    if (!total) return 0;
    return Math.round((answered / total) * 100);
  };

  const getExamTypeInfo = (examTypeId: string) => {
    return examTypes.find(type => type.exam_type_id === examTypeId);
  };

  const getExamTypeName = (examTypeId: string) => {
    const examType = getExamTypeInfo(examTypeId);
    return examType ? examType.name : examTypeId;
  };

  if (currentSession) {
    const isPaused = currentSession.status === 'paused';
    const progress = getProgressPercentage(currentSession.answeredQuestions, currentSession.totalQuestions);

    return (
      <div className="exam-status-card exam-status-card--active">
        <div className="exam-status-card__header">
          <div>
            <span className="exam-status-card__eyebrow">진행 중인 세션</span>
            <h3>{isPaused ? '일시정지된 시험' : '진행 중인 시험'}</h3>
          </div>
          <span className={`exam-status-card__badge ${isPaused ? 'exam-status-card__badge--paused' : 'exam-status-card__badge--active'}`}>
            {isPaused ? '일시정지' : '진행 중'}
          </span>
        </div>

        <div className="exam-status-card__body">
          <strong className="exam-status-card__title">{currentSession.examType ? getExamTypeName(currentSession.examType) : 'AWS 모의시험'}</strong>
          <div className="exam-status-card__stats">
            <span>답변 {currentSession.answeredQuestions}/{currentSession.totalQuestions}</span>
            <span>현재 {currentSession.currentQuestion}번</span>
            <span>남은 시간 {formatTime(currentSession.timeRemainingMinutes)}</span>
          </div>
          <div className="exam-status-card__progress" aria-label={`${progress}% 완료`}>
            <div style={{ width: `${progress}%` }} />
          </div>
          <span className="exam-status-card__progress-text">{progress}% 완료</span>
        </div>

        <button className="exam-status-card__action" onClick={() => onContinueExam(currentSession.sessionId)}>
          {isPaused ? '시험 재개하기' : '시험 계속하기'}
        </button>
      </div>
    );
  }

  if (examHistory.length > 0) {
    const latestExam = examHistory[0];
    const examTypeInfo = getExamTypeInfo(latestExam.examType);

    return (
      <div className="exam-status-card">
        <div className="exam-status-card__header">
          <div>
            <span className="exam-status-card__eyebrow">최근 결과</span>
            <h3>마지막 시험 결과</h3>
          </div>
          <span className={`exam-status-card__badge ${latestExam.passed === true ? 'exam-status-card__badge--passed' : 'exam-status-card__badge--failed'}`}>
            {latestExam.passed === true ? '합격' : '복습 필요'}
          </span>
        </div>

        <div className="exam-status-card__body">
          <strong className="exam-status-card__title">
            {examCodeOf(examTypeInfo) ? (
              <ExamBadge code={examCodeOf(examTypeInfo)} size="sm" />
            ) : examTypeInfo?.icon && (
              <img src={examTypeInfo.icon} alt="" className="exam-status-card__exam-icon" />
            )}
            {getExamTypeName(latestExam.examType)}
          </strong>
          <div className="exam-status-card__stats">
            <span>{latestExam.totalQuestions || 65}문제</span>
            <span>{Math.min(130, (latestExam.totalQuestions || 65) * 2)}분</span>
            <span>합격 기준 720점</span>
          </div>
          <p className="exam-status-card__summary">최근 점수: {latestExam.percentage}%</p>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-status-card">
      <div className="exam-status-card__header">
        <div>
          <span className="exam-status-card__eyebrow">다음 단계</span>
          <h3>시험 준비 완료</h3>
        </div>
        <span className="exam-status-card__badge exam-status-card__badge--ready">준비 완료</span>
      </div>

      <div className="exam-status-card__body">
        <strong className="exam-status-card__title">AWS 자격증 모의시험</strong>
        <div className="exam-status-card__stats">
          <span>응시 가능 시험 {examTypes.length}개</span>
          <span>시간 제한 모드</span>
          <span>결과 분석</span>
        </div>
      </div>

      <button
        className="exam-status-card__action"
        onClick={onStartExam}
        disabled={examTypes.length === 0}
      >
        모의시험 시작하기
      </button>
    </div>
  );
};

export default ExamStatusCard;
